import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import React from 'react';
import Logo from '../logo/Logo';
import FooterItemGrid from './footerItemGrid';
import Divider from '../divider/Divider';
import SocialsGroup from './socialsGroup';
import Image from '../custom_image/Image';
import ButtonGroup from './buttonGroup';
import FooterList from './FooterList';
import QuotingFooter from './QuotingFooter';
import AmpFeaturedSection from './AmpFeaturedSection';
import FooterSkeleton from './FooterSkeleton';
import { useMenuData } from './../../../hooks/useMenuData';

/**
* Footer component for rendering a custom footer with logo, links, buttons, and legal text.
* The layout and content of the footer adapt based on screen size using Material-UI's
* responsive utilities.
*/
export default function Footer({ actions }) {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('lm'));

  // Get and format footer menu data reactively - component updates when data arrives
  const footerMenu = useMenuData('footer_menu');
  const { columns, mobileSections, socialIcons, horizontalBar, legalText } = footerMenu;
  
  // Check if menu data is loaded (has actual content, not just empty state)
  const isMenuLoaded = columns.length > 0 || mobileSections.length > 0;
  
  const shouldRenderQuotingFooter = window.location.pathname.includes('/quoting');
  const shouldRenderAmpFeaturedSection = window.location.pathname.includes('/account-summary');

  // Show logo immediately for branding; skeleton only the menu-dependent content
  if (!isMenuLoaded) {
    return (
      <FooterSkeleton
        logo={
          <>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Logo alt="Custom Footer Logo" logoType="footer" logoheight="56px" logowidth="273px" />
            </Box>
            <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
              <Logo alt="Custom Footer Logo" logoType="footer" logoheight="50px" logowidth="244px" />
            </Box>
          </>
        }
      />
    );
  }

  return (
    <>
      {shouldRenderAmpFeaturedSection && <AmpFeaturedSection />}
      {shouldRenderQuotingFooter && <QuotingFooter />}
      <Box
        maxWidth="100%"
        disableGutters
        sx={{ backgroundColor: theme.palette.primary.main, overflow: 'hidden' }}
      >
        <Container maxWidth="100%" sx={{ p: "0 !important" }} >
          <Box
            sx={{
              [theme.breakpoints.up('md')]: { // This will apply the styles only on desktop screens (md and up)
                display: 'flex',
                flexDirection: 'column',
                pt: '66px',
                width: '100%',
                pb: '50px',
                position: 'relative',
              },
              [theme.breakpoints.up('xs')]: {
                pt: '50px',
              },
              pl: { xs: '26px', lg: '17.5%' },
              pr: { xs: '26px', lg: '11%' },
              boxSizing: "border-box"
            }}
          >
            <Box sx={{ display: 'inline-block', verticalAlign: 'top', width: '100%' }}>
              {/*For Desktop #Calcas Logo*/}
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Logo
                  alt="Custom Footer Logo"
                  logoType="footer"
                  logoheight="56px"
                  logowidth="273px"
                />
              </Box>
              {/*For Mobile #Calcas Logo*/}
              <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
                <Logo
                  alt="Custom Footer Logo"
                  logoType="footer"
                  logoheight="50px"
                  logowidth="244px"
                />
              </Box>
              {/*For Desktop #Vertical Menu*/}
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <FooterItemGrid columns={columns} />
              </Box>
              {/*For Mobile #Vertical Menu*/}
              <Box sx={{ display: { xs: 'block', lg: 'none' }, pt: '46px', pb: '56px' }}>
                <FooterList actions={actions} backgroundColor="transparent" sections={mobileSections} />
              </Box>
              {/*For Desktop #Social Icons*/}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  justifyContent: 'flex-end',
                  width: '100%',
                  marginTop: '-55px',
                  position: 'relative'
                }}
              >
                <SocialsGroup color="#68D3E3" size="34px" socialIcons={socialIcons} />
              </Box>
              {/*For Mobile #Social Icons*/}
              <Box
                sx={{
                  display: { xs: 'flex', lg: 'none' },
                  justifyContent: 'center',

                }}
              >
                <SocialsGroup
                  color="#68D3E3"
                  socialIcons={socialIcons}
                  size={isSmallMobile ? '20px' : '25px'}
                />
              </Box>
              <Box
                sx={{
                  display: { xs: 'block', lg: 'none' },
                }}
              >
                <ButtonGroup buttons={horizontalBar?.links || []} direction="column" />
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: { xs: "34px" } }}>
            <Divider backgroundColor="#FFFFFF" height=".5px" width="100%" />
          </Box>
          <Box
            sx={{
              [theme.breakpoints.down('md')]: { // Apply only for xs and smaller devices
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                py: { xs: 0 }, // Set vertical padding to 0 for xs
                padding: '48px 0 !important', // Apply padding for xs
                position: 'relative',
              }
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
                verticalAlign: 'top',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: { xs: '0', md: '46px' },
                  pl: { xs: '41px', lg: '17.5%' },
                  pr: { xs: '41px', lg: '11%' },
                  boxSizing: "border-box"
                }}
              >
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                  <ButtonGroup buttons={horizontalBar?.links || []} direction="row" spacing={3} />
                </Box>
                {horizontalBar?.image?.length > 0 &&
                  <Box sx={{
                    display: { xs: 'none', lg: 'block' },
                    p: { lg: '0px' },

                  }}>
                    <Image
                      alt="bbb-logo-comedy-guys-defensive-driving 2"
                      height="29px"
                      src={horizontalBar?.image}
                      width="107px"
                    />
                  </Box>
                }
              </Box>
              {legalText?.length > 0 &&
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: { xs: '0', md: 3 },
                    pl: { xs: '41px', lg: '17.5%' },
                    pr: { xs: '41px', lg: '11%' },
                    boxSizing: "border-box"
                  }}
                >
                  <Box sx={{ maxWidth: '910px', width: "100%", color: "#fff" }}>
                    <div className='footer-legal-text'
                      dangerouslySetInnerHTML={{
                        __html: legalText,
                      }}
                    />
                  </Box>
                </Box>
              }
              {horizontalBar?.image?.length > 0 &&
                <Box sx={{
                  display: { xs: 'flex', lg: 'none' },
                  mt: 3,
                  gap: 2.5,
                  flexDirection: isSmallMobile ? 'column' : 'row',
                  pl: { xs: '41px', lg: '17.5%' },
                  pr: { xs: '41px', lg: '11%' },
                }}>
                  <Image
                    alt="bbb-logo-comedy-guys-defensive-driving 2"
                    height="29px"
                    src={horizontalBar?.image}
                    width="107px"
                  />
                </Box>
              }
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
